import * as React from "react";
import { useState } from "react";
import axios from "axios";

import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch } from "../getConfig";

// Small page size so the pager shows for any user with more than a handful of rows.
const PAGE_SIZE = 10;

const useStyles = makeStyles(() => ({
  header: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    paddingTop: 20
  },
  page: {
    maxWidth: 1100,
    margin: "0 auto",
    minHeight: "calc(100vh - 3em)",
  },
  searchRow: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    marginBottom: "1.5em",
  },
  section: { marginBottom: "1.25em" },
  sectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  badge: { fontSize: "1.05rem", padding: "0.4em 0.2em", fontWeight: 600 },
  namesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "1em",
    marginTop: "0.75em",
  },
  nameCard: { border: "1px solid #e0e0e0", borderRadius: 6, padding: "0.75em" },
  pre: {
    background: "#f6f8fa",
    padding: "0.5em",
    borderRadius: 4,
    fontSize: "0.75rem",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
  },
  chipRow: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    marginTop: "0.5em",
  },
  actionRow: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    marginTop: "1em",
  },
}));

// Decision -> chip color (MUI v4 chip only supports default/primary/secondary; use style).
const DECISION_STYLE: Record<string, React.CSSProperties> = {
  pass: { backgroundColor: "#2e7d32", color: "#fff" },
  didit: { backgroundColor: "#1565c0", color: "#fff" },
  relink: { backgroundColor: "#ed6c02", color: "#fff" },
  blocked: { backgroundColor: "#c62828", color: "#fff" },
  manual_review: { backgroundColor: "#c62828", color: "#fff" },
  pending: { backgroundColor: "#757575", color: "#fff" },
};

const chipStyle = (r: any): React.CSSProperties => {
  if (r === "pass" || r === true)
    return { backgroundColor: "#2e7d32", color: "#fff" };
  if (r === "fail" || r === false)
    return { backgroundColor: "#c62828", color: "#fff" };
  if (r === "refer") return { backgroundColor: "#ed6c02", color: "#fff" };
  return { backgroundColor: "#e0e0e0" };
};

// Prev/Next pager driven by the standard pagination metadata.
const Pager: React.FC<{ pagination: any; onPage: (p: number) => void }> = ({
  pagination,
  onPage,
}) => {
  if (!pagination || pagination.total_pages <= 1) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        alignItems: "center",
        marginTop: "0.5em",
      }}
    >
      <Button
        size="small"
        disabled={!pagination.has_previous_page}
        onClick={() => onPage(pagination.page - 1)}
      >
        Prev
      </Button>
      <Typography variant="caption">
        Page {pagination.page} / {pagination.total_pages} · {pagination.total}{" "}
        total
      </Typography>
      <Button
        size="small"
        disabled={!pagination.has_next_page}
        onClick={() => onPage(pagination.page + 1)}
      >
        Next
      </Button>
    </div>
  );
};

const Section: React.FC<{
  title: string;
  classes: any;
  defaultOpen?: boolean;
}> = ({ title, classes, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className={classes.section}>
      <CardContent>
        <div className={classes.sectionHead} onClick={() => setOpen((o) => !o)}>
          <Typography variant="h6">{title}</Typography>
          <Button size="small">{open ? "Hide" : "Show"}</Button>
        </div>
        <Collapse in={open}>
          <div style={{ marginTop: "0.5em" }}>{children}</div>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const MortgageKycDebug = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [didit, setDidit] = useState<any>(null);
  const [claims, setClaims] = useState<any>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [changes, setChanges] = useState<any[]>([]);
  const [decisionNote, setDecisionNote] = useState("");
  const [expandedEvent, setExpandedEvent] = useState<number | null>(null);
  const [eventsPage, setEventsPage] = useState<any>(null);
  const [diditPage, setDiditPage] = useState<any>(null);
  // Didit's 3 sub-lists page independently; hold each current page so a request for
  // one doesn't reset the others (backend defaults an omitted page param back to 1).
  const [diditPages, setDiditPages] = useState({
    verPage: 1,
    docPage: 1,
    logPage: 1,
  });
  const [reviewsPage, setReviewsPage] = useState<any>(null);
  const [changesPage, setChangesPage] = useState<any>(null);
  const [claimsPage, setClaimsPage] = useState<any>(null);

  // Fetch a specific page of one paginated list section and update its state.
  const loadPage = async (
    endpoint: string,
    page: number,
    setData: (d: any) => void,
    setPage: (p: any) => void,
    unwrap?: (d: any) => any,
  ) => {
    try {
      const res: any = await axios.get(
        endpoint,
        getConfigGet({ email, page, pageSize: PAGE_SIZE }),
      );
      const payload = unwrap ? unwrap(res.data?.data) : res.data?.data;
      setData(payload || []);
      setPage(res.data?.pagination || null);
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Failed to load page",
        "error",
        "",
        undefined,
        5000,
      );
    }
  };

  // Didit has 3 independently-paginated sub-lists. Send all 3 page params on every
  // request so paging one list doesn't reset the other two back to page 1.
  const loadDiditPage = async (
    which: "verPage" | "docPage" | "logPage",
    page: number,
  ) => {
    const nextPages = { ...diditPages, [which]: page };
    try {
      const res: any = await axios.get(
        ENDPOINTS.MORTGAGE_KYC_DIDIT,
        getConfigGet({ email, pageSize: PAGE_SIZE, ...nextPages }),
      );
      setDidit(res.data?.data);
      setDiditPage(res.data?.pagination || null);
      setDiditPages(nextPages);
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Failed to load page",
        "error",
        "",
        undefined,
        5000,
      );
    }
  };

  const load = async () => {
    if (!email) {
      notify("Enter a user email", "warning");
      return;
    }
    setLoading(true);
    setOverview(null);
    setDiditPages({ verPage: 1, docPage: 1, logPage: 1 });
    try {
      const cfg = getConfigGet({ email, pageSize: PAGE_SIZE });
      const [ov, ev, dd, cl, rv, ch]: any[] = await Promise.all([
        axios.get(ENDPOINTS.MORTGAGE_KYC_OVERVIEW, cfg),
        axios.get(ENDPOINTS.MORTGAGE_KYC_EVENTS, cfg),
        axios.get(ENDPOINTS.MORTGAGE_KYC_DIDIT, cfg),
        axios.get(ENDPOINTS.MORTGAGE_KYC_CLAIMS, cfg),
        axios.get(ENDPOINTS.MORTGAGE_KYC_REVIEWS, cfg),
        axios.get(ENDPOINTS.MORTGAGE_KYC_CHANGES, cfg),
      ]);
      setOverview(ov.data);
      setEvents(ev.data?.data || []);
      setEventsPage(ev.data?.pagination || null);
      setDidit(dd.data?.data);
      setDiditPage(dd.data?.pagination || null);
      setClaims(cl.data?.data);
      setClaimsPage(cl.data?.pagination || null);
      setReviews(rv.data?.data || []);
      setReviewsPage(rv.data?.pagination || null);
      setChanges(ch.data?.data || []);
      setChangesPage(ch.data?.pagination || null);
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Failed to load KYC data",
        "error",
        "",
        undefined,
        5000,
      );
    } finally {
      setLoading(false);
    }
  };

  const setDecision = async (decision: string) => {
    if (!window.confirm(`Set KYC decision to "${decision}" for ${email}?`))
      return;
    try {
      await axios.patch(
        ENDPOINTS.MORTGAGE_KYC_STATUS,
        { email, decision, note: decisionNote || undefined },
        { headers: getConfigPatch() },
      );
      notify(`Decision set to ${decision}`, "info");
      load();
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Failed to set decision",
        "error",
        "",
        undefined,
        5000,
      );
    }
  };

  const viewDocument = async (docId: number) => {
    try {
      const res: any = await axios.get(
        `${ENDPOINTS.MORTGAGE_KYC_DOCUMENT}/${docId}`,
        {
          headers: getConfigPatch(),
        },
      );
      if (res.data?.url) window.open(res.data.url, "_blank");
      else notify("Document unavailable", "warning");
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Failed to load document",
        "error",
        "",
        undefined,
        5000,
      );
    }
  };

  const status = overview?.status;
  const names = overview?.names;

  return (
    <div>
      <div className={classes.header}>
        <div>
          <Typography variant="h5" gutterBottom>
            Mortgage KYC
          </Typography>
        </div>
        <div className={classes.searchRow}>
          <TextField
            label="User email"
            value={email}
            onChange={(e) => setEmail(e.target.value.trim())}
            onKeyDown={(e) => e.key === "Enter" && load()}
            style={{ minWidth: 320 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={load}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Load"}
          </Button>
        </div>
      </div>
      <div className={classes.page}>
        {overview && (
          <>
            {/* Decision + why */}
            <Section title="Decision" classes={classes}>
              {status ? (
                <>
                  <Chip
                    className={classes.badge}
                    label={status.decision}
                    style={DECISION_STYLE[status.decision] || {}}
                  />
                  <Typography style={{ marginTop: "0.5em" }}>
                    <b>Reason:</b> {status.reason || "-"}
                  </Typography>
                  <Typography color="textSecondary">
                    {status.message}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Updated: {status.updatedAt}
                  </Typography>
                </>
              ) : (
                <Typography color="textSecondary">
                  No KYC decision recorded.
                </Typography>
              )}
            </Section>

            <Section title="Why this decision (name match)" classes={classes}>
              <div className={classes.namesGrid}>
                <div className={classes.nameCard}>
                  <Typography variant="subtitle2">A — Bank account</Typography>
                  <Typography>{names?.bank || "-"}</Typography>
                </div>
                <div className={classes.nameCard}>
                  <Typography variant="subtitle2">B — App / signup</Typography>
                  <Typography>
                    {[names?.app?.first_name, names?.app?.last_name]
                      .filter(Boolean)
                      .join(" ") || "-"}
                  </Typography>
                </div>
                <div className={classes.nameCard}>
                  <Typography variant="subtitle2">
                    C — Mortgage / bureau
                  </Typography>
                  <Typography>
                    {[names?.mortgage?.first_name, names?.mortgage?.last_name]
                      .filter(Boolean)
                      .join(" ") || "-"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {names?.mortgage?.lender || "-"} · bal{" "}
                    {names?.mortgage?.current_balance_amount ?? "-"} ·{" "}
                    {names?.mortgage?.joint_mortgage ? "joint" : "sole"} ·{" "}
                    {names?.mortgage?.data_source || "-"}
                  </Typography>
                </div>
              </div>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: "0.75em" }}
              >
                has_credit_data: {String(overview?.has_credit_data)}
              </Typography>
              {overview?.latest_match && (
                <pre className={classes.pre}>
                  {JSON.stringify(overview.latest_match, null, 2)}
                </pre>
              )}
            </Section>

            {/* Timeline */}
            <Section
              title={`Timeline${eventsPage ? ` (${eventsPage.total})` : ""}`}
              classes={classes}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Decision</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((e) => (
                    <React.Fragment key={e.id}>
                      <TableRow>
                        <TableCell>{e.createdAt}</TableCell>
                        <TableCell>{e.event_type}</TableCell>
                        <TableCell>{e.decision || "-"}</TableCell>
                        <TableCell>{e.reason || "-"}</TableCell>
                        <TableCell>{e.source || "-"}</TableCell>
                        <TableCell>
                          {e.event_data && (
                            <Button
                              size="small"
                              onClick={() =>
                                setExpandedEvent(
                                  expandedEvent === e.id ? null : e.id,
                                )
                              }
                            >
                              {expandedEvent === e.id ? "-" : "data"}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {expandedEvent === e.id && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <pre className={classes.pre}>
                              {JSON.stringify(e.event_data, null, 2)}
                            </pre>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              <Pager
                pagination={eventsPage}
                onPage={(p) =>
                  loadPage(
                    ENDPOINTS.MORTGAGE_KYC_EVENTS,
                    p,
                    setEvents,
                    setEventsPage,
                  )
                }
              />
            </Section>

            {/* Didit */}
            <Section title="Didit" classes={classes}>
              {didit?.verifications?.map((v: any) => (
                <div
                  key={v.id}
                  className={classes.nameCard}
                  style={{ marginBottom: "0.75em" }}
                >
                  <Typography variant="subtitle2">
                    Verification #{v.id} — status: {v.status} · attempts:{" "}
                    {v.attempt_count}
                  </Typography>
                  <div className={classes.chipRow}>
                    <Chip
                      size="small"
                      label={`doc: ${v.doc_verify_result || "-"}`}
                      style={chipStyle(v.doc_verify_result)}
                    />
                    <Chip
                      size="small"
                      label={`liveness: ${v.liveness_result || "-"}`}
                      style={chipStyle(v.liveness_result)}
                    />
                    <Chip
                      size="small"
                      label={`face: ${v.face_match_result || "-"}`}
                      style={chipStyle(v.face_match_result)}
                    />
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    {[v.first_name, v.last_name].filter(Boolean).join(" ")} ·{" "}
                    {v.dob || "-"} · {v.document_type || "-"}
                  </Typography>
                  {v.result_payload && (
                    <pre className={classes.pre}>
                      {JSON.stringify(v.result_payload, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
              <Pager
                pagination={diditPage?.verifications}
                onPage={(p) => loadDiditPage("verPage", p)}
              />

              {!!didit?.documents?.length && (
                <>
                  <Typography
                    variant="subtitle2"
                    style={{ marginTop: "0.5em" }}
                  >
                    Documents
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Kind</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {didit.documents.map((d: any) => (
                        <TableRow key={d.id}>
                          <TableCell>{d.doc_kind}</TableCell>
                          <TableCell>{d.mime_type}</TableCell>
                          <TableCell>{d.byte_size}</TableCell>
                          <TableCell>{d.createdAt}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              color="primary"
                              onClick={() => viewDocument(d.id)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Pager
                    pagination={diditPage?.documents}
                    onPage={(p) => loadDiditPage("docPage", p)}
                  />
                </>
              )}

              {!!didit?.api_logs?.length && (
                <>
                  <Typography
                    variant="subtitle2"
                    style={{ marginTop: "0.75em" }}
                  >
                    API logs
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Endpoint</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Latency</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {didit.api_logs.map((l: any) => (
                        <TableRow key={l.id}>
                          <TableCell>{l.createdAt}</TableCell>
                          <TableCell>{l.endpoint}</TableCell>
                          <TableCell>{l.status_code ?? "-"}</TableCell>
                          <TableCell>{l.latency_ms ?? "-"}</TableCell>
                          <TableCell>{l.error_message || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Pager
                    pagination={diditPage?.api_logs}
                    onPage={(p) => loadDiditPage("logPage", p)}
                  />
                </>
              )}
            </Section>

            {/* Claims */}
            <Section
              title="Identity claims"
              classes={classes}
              defaultOpen={false}
            >
              <Typography variant="subtitle2">This user</Typography>
              <pre className={classes.pre}>
                {JSON.stringify(claims?.own, null, 2)}
              </pre>
              <Typography variant="subtitle2">
                Co-claimants (same mortgage)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(claims?.co_claimants || []).map((c: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{c.user_id}</TableCell>
                      <TableCell>{c.name_normalised}</TableCell>
                      <TableCell>{c.status}</TableCell>
                      <TableCell>{c.createdAt}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pager
                pagination={claimsPage}
                onPage={(p) =>
                  loadPage(
                    ENDPOINTS.MORTGAGE_KYC_CLAIMS,
                    p,
                    setClaims,
                    setClaimsPage,
                  )
                }
              />
            </Section>

            {/* Manual review + action */}
            <Section
              title="Manual review"
              classes={classes}
              defaultOpen={false}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Resolved by</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviews.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.reason}</TableCell>
                      <TableCell>{r.status}</TableCell>
                      <TableCell>{r.resolved_by || "-"}</TableCell>
                      <TableCell>{r.resolution_note || "-"}</TableCell>
                      <TableCell>{r.createdAt}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pager
                pagination={reviewsPage}
                onPage={(p) =>
                  loadPage(
                    ENDPOINTS.MORTGAGE_KYC_REVIEWS,
                    p,
                    setReviews,
                    setReviewsPage,
                  )
                }
              />

              <div className={classes.actionRow}>
                <TextField
                  label="Note (optional)"
                  value={decisionNote}
                  onChange={(e) => setDecisionNote(e.target.value)}
                  style={{ minWidth: 280 }}
                />
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#2e7d32", color: "#fff" }}
                  onClick={() => setDecision("pass")}
                >
                  Approve → Pass
                </Button>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#c62828", color: "#fff" }}
                  onClick={() => setDecision("blocked")}
                >
                  Reject → Blocked
                </Button>
              </div>
            </Section>

            {/* Identity changes */}
            <Section
              title="Identity changes"
              classes={classes}
              defaultOpen={false}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Old</TableCell>
                    <TableCell>New</TableCell>
                    <TableCell>Changed by</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changes.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.field}</TableCell>
                      <TableCell>{c.old_value || "-"}</TableCell>
                      <TableCell>{c.new_value || "-"}</TableCell>
                      <TableCell>{c.changed_by}</TableCell>
                      <TableCell>{c.createdAt}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pager
                pagination={changesPage}
                onPage={(p) =>
                  loadPage(
                    ENDPOINTS.MORTGAGE_KYC_CHANGES,
                    p,
                    setChanges,
                    setChangesPage,
                  )
                }
              />
            </Section>
          </>
        )}
        <Notification />
      </div>
    </div>
  );
};

export default MortgageKycDebug;
